import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { SITE } from '../content';
import { Popups, storage } from '../popups';

type Tab = 'signin' | 'signup';
type Method = 'phone' | 'email';
type View = 'form' | 'otp' | 'done';
/** who the account is for */
type Kind = 'wholesaler' | 'dealer' | 'online';
type Field = 'phone' | 'name' | 'business' | 'email' | 'password' | 'confirm';
type Provider = 'Google' | 'Apple';

/**
 * Stands in for the server, which this page does not have: nothing below
 * signs anyone in. The one-time code is fixed and checked in the browser, the
 * Google and Apple buttons only wait a moment, and no account is created.
 */
const DEMO = {
  otp: '123456',
  resendSeconds: 30,
  /** how long a pretended request takes, in ms */
  latency: 700,
  account: { Google: 'you@gmail.com', Apple: 'you@icloud.com' },
};

/** localStorage: the pretended session — who "signed in" here last */
const SESSION_KEY = 'lnAuthUser';
const OTP_LENGTH = 6;

/** The three kinds of account, in the order of their buttons: each with its icon, path data for a 16×16 stroke icon. */
const KINDS: { id: Kind; label: string; icon: string }[] = [
  { id: 'wholesaler', label: 'Wholesalers', icon: 'M1.5 6.3 8 2.5l6.5 3.8v7.2h-13zM4.5 13.5v-5h7v5M4.5 11h7' },
  { id: 'dealer', label: 'Dealers', icon: 'M2 6.5 3.2 2.5h9.6L14 6.5M2.6 6.5v7h10.8v-7M2 6.5h12M6.5 13.5v-4h3v4' },
  {
    id: 'online',
    label: 'Online',
    icon: 'M8 14.3A6.3 6.3 0 1 0 8 1.7a6.3 6.3 0 0 0 0 12.6ZM1.7 8h12.6M8 1.7c1.7 1.9 2.6 4 2.6 6.3s-.9 4.4-2.6 6.3C6.3 12.4 5.4 10.3 5.4 8s.9-4.4 2.6-6.3Z',
  },
];

const COPY = {
  signin: {
    head: 'Welcome back',
    sub: 'Sign in to track your orders, keep your wishlist and check out faster.',
    title: 'Sign in to Little Nap',
    lede: 'Use the method you signed up with.',
    social: 'Sign in with',
    phoneBtn: 'Send OTP',
    emailBtn: 'Sign In',
    emailBusy: 'Signing in…',
    altText: 'New to Little Nap?',
    altBtn: 'Create an account',
  },
  signup: {
    head: 'Create your Little Nap account',
    sub: 'One account for your orders, warranty registration and online-only offers.',
    title: 'Sign up in seconds',
    lede: 'Pick any one - you can add the others later.',
    social: 'Sign up with',
    phoneBtn: 'Send OTP',
    emailBtn: 'Create Account',
    emailBusy: 'Creating account…',
    altText: 'Already have an account?',
    altBtn: 'Sign in',
  },
};

const digits = (text: string) => text.replace(/\D/g, '');

/** 0 for no password, then 1 (weak) to 4 (strong). */
function passwordScore(password: string): number {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password) || password.length >= 12) score++;
  return password ? Math.max(1, score) : 0;
}

/**
 * Sign In and Sign Up in one centre popup, opened by the header's Login
 * button: Google, Apple, phone with a one-time code, or email and password —
 * for a wholesaler's, a dealer's or an online customer's account.
 *
 * It is the front end only (see DEMO): wire `sendOtp`, `verifyOtp`,
 * `submitEmail` and `social` to the real service before this is a login.
 */
@Component({
  selector: 'app-auth-popup',
  templateUrl: './auth-popup.html',
  styleUrl: './auth-popup.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthPopup {
  protected readonly popups = inject(Popups);
  protected readonly kinds = KINDS;
  protected readonly otpBoxes = Array.from({ length: OTP_LENGTH }, (_, i) => i);
  protected readonly demoOtp = DEMO.otp;
  protected readonly termsHref = `${SITE}/terms-and-conditions`;
  protected readonly privacyHref = `${SITE}/privacy-policy`;

  protected readonly open = computed(() => this.popups.open() === 'auth');
  protected readonly tab = signal<Tab>('signup');
  protected readonly method = signal<Method>('phone');
  protected readonly view = signal<View>('form');
  protected readonly kind = signal<Kind>('online');
  protected readonly copy = computed(() => COPY[this.tab()]);

  /** what is wrong with each field, shown under it */
  protected readonly errors = signal<Partial<Record<Field, string>>>({});
  protected readonly consentMissing = signal(false);
  protected readonly otpError = signal('');
  /** the button whose pretended request is under way */
  protected readonly busy = signal<'submit' | 'verify' | Provider | null>(null);
  /** the number the code went to */
  protected readonly sentTo = signal('');
  /** seconds until the code may be sent again */
  protected readonly resendIn = signal(0);
  protected readonly score = signal(0);
  protected readonly scoreLabel = computed(() => ['', 'Weak', 'Fair', 'Good', 'Strong'][this.score()]);
  protected readonly passwordShown = signal(false);
  /** the closing message: its heading, and the sentence around the account's address or number */
  protected readonly done = signal({ title: '', before: '', label: '', after: '' });

  protected readonly submitLabel = computed(() => {
    const copy = this.copy();
    if (this.method() === 'phone') return this.busy() === 'submit' ? 'Sending OTP…' : copy.phoneBtn;
    return this.busy() === 'submit' ? copy.emailBusy : copy.emailBtn;
  });

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly code = viewChild.required<ElementRef<HTMLSelectElement>>('code');
  private readonly phone = viewChild.required<ElementRef<HTMLInputElement>>('phone');
  private readonly name = viewChild.required<ElementRef<HTMLInputElement>>('name');
  private readonly business = viewChild.required<ElementRef<HTMLInputElement>>('business');
  private readonly email = viewChild.required<ElementRef<HTMLInputElement>>('email');
  private readonly password = viewChild.required<ElementRef<HTMLInputElement>>('password');
  private readonly confirm = viewChild.required<ElementRef<HTMLInputElement>>('confirm');
  private readonly consent = viewChild.required<ElementRef<HTMLInputElement>>('consent');
  private readonly otp = viewChildren<ElementRef<HTMLInputElement>>('otp');
  private timer = 0;
  /** the name given on Sign Up by phone, kept while the code is on its way */
  private pendingName = '';

  constructor() {
    // it opens on Sign Up for a newcomer and on Sign In for someone who has been here, always by phone;
    // and closing it stops the resend countdown
    effect(() => {
      const open = this.open();
      untracked(() => {
        clearInterval(this.timer);
        if (!open) return;
        this.setTab(storage.get('local', SESSION_KEY) ? 'signin' : 'signup');
        this.setMethod('phone');
      });
    });

    afterNextRender(() => {
      const session = this.session();
      if (!session) return;
      this.popups.account.set(session.name || 'Account');
      const kind = KINDS.find((entry) => entry.id === session.kind);
      if (kind) this.kind.set(kind.id);
    });
  }

  protected setTab(tab: Tab): void {
    this.tab.set(tab);
    this.clearErrors();
    this.view.set('form');
  }

  protected setMethod(method: Method): void {
    this.method.set(method);
    this.clearErrors();
  }

  /** The one main button serves all four combinations of tab and method. */
  protected submit(): void {
    if (this.method() === 'phone') this.sendOtp();
    else this.submitEmail();
  }

  protected onPhoneInput(): void {
    const phone = this.phone().nativeElement;
    phone.value = digits(phone.value).slice(0, this.code().nativeElement.value === '+91' ? 10 : 15);
    this.errors.update((errors) => ({ ...errors, phone: '' }));
  }

  protected clearError(field: Field): void {
    this.errors.update((errors) => ({ ...errors, [field]: '' }));
  }

  protected onPasswordInput(): void {
    this.score.set(passwordScore(this.password().nativeElement.value));
  }

  /** "Forgot password?" on Sign In: the one-time code is the way back in. */
  protected useOtp(): void {
    this.setMethod('phone');
    this.focusSoon(this.phone().nativeElement);
  }

  protected backToForm(focusPhone: boolean): void {
    clearInterval(this.timer);
    this.view.set('form');
    if (focusPhone) this.focusSoon(this.phone().nativeElement);
  }

  // ── the one-time code: six boxes that behave as one field ──

  protected onOtpInput(index: number): void {
    const boxes = this.otpInputs();
    boxes[index].value = digits(boxes[index].value).slice(-1);
    if (boxes[index].value && index < OTP_LENGTH - 1) boxes[index + 1].focus();
    this.otpError.set('');
    if (this.otpValue().length === OTP_LENGTH) this.verifyOtp();
  }

  protected onOtpKey(event: KeyboardEvent, index: number): void {
    const boxes = this.otpInputs();
    if (event.key === 'Backspace' && !boxes[index].value && index > 0) {
      event.preventDefault();
      boxes[index - 1].focus();
      boxes[index - 1].value = '';
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      boxes[index - 1].focus();
    } else if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      event.preventDefault();
      boxes[index + 1].focus();
    }
  }

  protected onOtpPaste(event: ClipboardEvent): void {
    const text = digits(event.clipboardData?.getData('text') ?? '');
    if (!text) return;
    event.preventDefault();
    const boxes = this.otpInputs();
    [...text.slice(0, OTP_LENGTH)].forEach((digit, i) => (boxes[i].value = digit));
    boxes[Math.min(text.length, OTP_LENGTH - 1)].focus();
    if (this.otpValue().length === OTP_LENGTH) this.verifyOtp();
  }

  protected resend(): void {
    this.clearOtp();
    this.otpError.set('');
    this.startResendTimer();
    this.otpInputs()[0].focus();
  }

  // ── the flows: each one only pretends (see DEMO) ──

  private sendOtp(): void {
    this.clearErrors();
    const signingUp = this.tab() === 'signup';
    const phone = this.phone().nativeElement;

    const errors: Partial<Record<Field, string>> = signingUp ? this.profileErrors() : {};
    const phoneError = this.phoneError();
    if (phoneError) errors.phone = phoneError;
    if (Object.keys(errors).length) {
      this.errors.set(errors);
      const first = errors.name ? this.name() : errors.business ? this.business() : null;
      (first?.nativeElement ?? phone).focus();
      return;
    }
    if (signingUp && !this.consent().nativeElement.checked) {
      this.consentMissing.set(true);
      this.consent().nativeElement.focus();
      return;
    }

    this.pendingName = signingUp ? this.name().nativeElement.value.trim() : '';
    this.sentTo.set(`${this.code().nativeElement.value} ${digits(phone.value)}`);
    this.busy.set('submit');
    setTimeout(() => {
      this.busy.set(null);
      this.clearOtp();
      this.view.set('otp');
      this.focusSoon(this.otpInputs()[0]);
      this.startResendTimer();
    }, DEMO.latency);
  }

  protected verifyOtp(): void {
    const code = this.otpValue();
    if (code.length !== OTP_LENGTH) return;
    this.busy.set('verify');
    setTimeout(() => {
      this.busy.set(null);
      if (code !== DEMO.otp) {
        this.otpError.set('That code is not right. Please check and try again.');
        this.clearOtp();
        this.otpInputs()[0].focus();
        return;
      }
      this.finish('Phone', this.sentTo(), this.pendingName);
    }, DEMO.latency);
  }

  private submitEmail(): void {
    this.clearErrors();
    const signingUp = this.tab() === 'signup';
    const name = this.name().nativeElement.value.trim();
    const email = this.email().nativeElement.value.trim();
    const password = this.password().nativeElement.value;

    const errors: Partial<Record<Field, string>> = signingUp ? this.profileErrors() : {};
    if (!email) errors.email = 'Enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errors.email = 'Enter a valid email address.';
    if (!password) errors.password = 'Enter your password.';
    else if (signingUp && (password.length < 8 || !/\d/.test(password) || !/[A-Za-z]/.test(password))) {
      errors.password = 'Use at least 8 characters with a letter and a number.';
    }
    if (signingUp && !errors.password && this.confirm().nativeElement.value !== password) {
      errors.confirm = 'The passwords do not match.';
    }
    const consentMissing = signingUp && !this.consent().nativeElement.checked;

    this.errors.set(errors);
    this.consentMissing.set(consentMissing);
    if (Object.keys(errors).length || consentMissing) {
      const first = errors.name
        ? this.name()
        : errors.business
          ? this.business()
          : errors.email
            ? this.email()
            : errors.password
              ? this.password()
              : errors.confirm
                ? this.confirm()
                : null;
      first?.nativeElement.focus();
      return;
    }

    this.busy.set('submit');
    setTimeout(() => {
      this.busy.set(null);
      this.finish('Email', email, name);
    }, DEMO.latency);
  }

  /** A real build hands over to the provider here (a redirect, or its own pop-up window). */
  protected social(provider: Provider): void {
    this.clearErrors();
    this.busy.set(provider);
    setTimeout(() => {
      this.busy.set(null);
      this.finish(provider, DEMO.account[provider]);
    }, DEMO.latency + 300);
  }

  /** Shows the closing message and remembers the pretended session, which the header's Login button then shows. */
  private finish(via: string, label: string, name = ''): void {
    const kind = this.kind();
    // (an online customer's account is simply "your account")
    const whose = kind === 'online' ? 'Your account' : `Your ${kind} account`;
    this.done.set(
      this.tab() === 'signup'
        ? { title: 'Account created', before: `Welcome to Little Nap. ${whose} is linked to `, label, after: ` via ${via}.` }
        : {
            title: 'You’re signed in',
            before: kind === 'online' ? `Signed in with ${via} – ` : `Signed in to your ${kind} account with ${via} – `,
            label,
            after: '.',
          },
    );
    this.view.set('done');
    this.focusSoon(this.host.querySelector<HTMLElement>('.view--done .submit'));

    const who = name || (via === 'Phone' ? digits(label).slice(-10) : label.split('@')[0]) || 'Account';
    storage.set('local', SESSION_KEY, JSON.stringify({ via, label, name: who, kind, at: Date.now() }));
    this.popups.account.set(who);
  }

  private session(): { name?: string; kind?: Kind } | null {
    try {
      return JSON.parse(storage.get('local', SESSION_KEY) ?? 'null');
    } catch {
      return null;
    }
  }

  /** What Sign Up asks for whichever method is used: a name, and a shop or firm name for a trade account. */
  private profileErrors(): Partial<Record<Field, string>> {
    const errors: Partial<Record<Field, string>> = {};
    if (this.name().nativeElement.value.trim().length < 2) errors.name = 'Enter your full name.';
    if (this.kind() !== 'online' && this.business().nativeElement.value.trim().length < 2) {
      errors.business = this.kind() === 'wholesaler' ? 'Enter your firm name.' : 'Enter your shop name.';
    }
    return errors;
  }

  private phoneError(): string {
    const number = digits(this.phone().nativeElement.value);
    const india = this.code().nativeElement.value === '+91';
    if (!number) return 'Enter your mobile number.';
    if (india && number.length !== 10) return 'Indian mobile numbers are 10 digits.';
    if (india && !/^[6-9]/.test(number)) return 'Indian mobile numbers start with 6-9.';
    if (number.length < 6 || number.length > 15) return 'Enter a valid mobile number.';
    return '';
  }

  private clearErrors(): void {
    this.errors.set({});
    this.consentMissing.set(false);
    this.otpError.set('');
  }

  private otpInputs(): HTMLInputElement[] {
    return this.otp().map((box) => box.nativeElement);
  }

  private otpValue(): string {
    return this.otpInputs()
      .map((box) => box.value)
      .join('');
  }

  private clearOtp(): void {
    for (const box of this.otpInputs()) box.value = '';
  }

  private startResendTimer(): void {
    clearInterval(this.timer);
    this.resendIn.set(DEMO.resendSeconds);
    this.timer = window.setInterval(() => {
      this.resendIn.update((left) => left - 1);
      if (this.resendIn() <= 0) clearInterval(this.timer);
    }, 1000);
  }

  /** Focus, once the view it is in has been shown. */
  private focusSoon(target: HTMLElement | null): void {
    setTimeout(() => target?.focus(), 40);
  }
}
